import { helpArticleUrls } from "config/helpArticleUrls";
import { Button } from "components/common/buttons/Button";
import { BudgetSummaryCard as KpiCard } from "../BudgetSummaryCard";
import { BudgetHealthHeaderProps } from "./BudgetHealthHeader.types";
import { formatBudgetMoney } from "../formatBudgetMoney";
import { formatActualsToDatePercent } from "./BudgetHealthHeader.utils";

function formatMoney(currencySymbol: string, value: number): string {
  // These are known totals (grand total, actuals, variance, ATL/BTL, etc.),
  // always a real number - including a legitimate 0 for an empty/blank
  // budget - never "not yet entered". So always render the amount (e.g.
  // "$0.00"), without the emptyIfNoValue blanking meant for optional values.
  return formatBudgetMoney(currencySymbol, value);
}

export const BudgetHealthHeader = ({
  metrics,
  currencySymbol,
  onOverageClick,
}: BudgetHealthHeaderProps) => {
  const remainingTone =
    metrics.totalVariance > 0 ? "positive" : metrics.totalVariance < 0 ? "negative" : "default";

  return (
    <div className="mb-3 flex flex-col gap-2" aria-label="Budget health summary">
      <div className="flex flex-wrap gap-2">
        <KpiCard
          label="Grand Total"
          value={formatMoney(currencySymbol, metrics.grandTotal)}
          subValue={
            metrics.taxNetting
              ? `Net ${formatMoney(currencySymbol, metrics.taxNetting.netCost)} after incentives`
              : undefined
          }
        />
        <KpiCard
          label="Cost / Shoot Day"
          value={
            metrics.costPerShootDay != null
              ? formatMoney(currencySymbol, metrics.costPerShootDay)
              : "—"
          }
        />
        <KpiCard
          label="Contingency"
          value={`${metrics.contingencyPercent}%`}
          subValue={formatMoney(currencySymbol, metrics.contingencyAmount)}
        />
        <KpiCard
          label="ATL / BTL"
          value={`${metrics.atlSharePercent.toFixed(0)}% / ${metrics.btlSharePercent.toFixed(0)}%`}
          subValue={`${formatMoney(currencySymbol, metrics.atlEstimate)} ATL · ${formatMoney(currencySymbol, metrics.btlEstimate)} BTL`}
          tooltip="ATL (above the line) is creative principals—writers, producers, directors, and principal cast. BTL (below the line) is crew, equipment, locations, post, and other physical production costs."
          helpHref={helpArticleUrls.budgetHealthMetrics}
        />
        <KpiCard
          label="Actuals to date"
          value={`${formatActualsToDatePercent(metrics.actualsToDatePercent)}%`}
          subValue={formatMoney(currencySymbol, metrics.totalActual)}
          tooltip="Actuals entered on line items as a share of the grand total estimate. This is not committed cost (POs / deal memos)—that is a separate cost-control feature."
          helpHref={helpArticleUrls.budgetActuals}
        />
        <KpiCard
          label="Remaining vs estimate"
          value={formatMoney(currencySymbol, metrics.totalVariance)}
          tone={remainingTone}
          tooltip="Estimate minus actuals to date. Positive means under the estimate; negative means over. Full cost variance (budget vs estimated final cost) requires commitments and ETC."
          helpHref={helpArticleUrls.budgetHealthMetrics}
        />
      </div>

      {metrics.topOverages.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] text-muted-foreground">Top overages:</span>
          {metrics.topOverages.map((overage) => (
            <Button
              key={overage.subCategoryId}
              type="button"
              color="red"
              size="xs"
              className="h-auto rounded-full px-2 py-0.5 text-[11px]"
              onClick={() => onOverageClick?.(overage.subCategoryId)}
            >
              {overage.name} +{formatMoney(currencySymbol, overage.overageAmount)}
            </Button>
          ))}
        </div>
      ) : null}
    </div>
  );
};
